import React, { useEffect, useState } from "react";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { canDelete } from "../utils/permissions";

const API_BASE = API_ORIGIN;

// Same icon set as UsersList.jsx / DepartmentManagement.jsx's Action
// column (no shared icons module in this project yet -- each management
// page defines its own small copy, matching the existing convention).
const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="10" y1="11" x2="10" y2="17" strokeLinecap="round" />
    <line x1="14" y1="11" x2="14" y2="17" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// TEMP: this page was showing a blank white screen for the user in
// production -- something in RoleManagementImpl below throws at
// runtime, but the exact error hasn't been diagnosed yet (would need
// the browser console output). Per request, swapping the real page out
// for this placeholder for now rather than shipping a blank screen.
// RoleManagementImpl and everything it depends on is untouched below --
// once the console error is known and fixed, restore this by renaming
// RoleManagementImpl back to RoleManagement and deleting this wrapper
// (and the "export default" above RoleManagementImpl).
export default function RoleManagement() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body text-center py-5">
        <i className="ti ti-rocket fs-1 text-muted d-block mb-3" />
        <h5 className="text-muted mb-0">This Page is Part of next release</h5>
      </div>
    </div>
  );
}

function RoleManagementImpl() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleName, setRoleName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [viewing, setViewing] = useState(null); // role object being viewed, or null

  const loadRoles = () => {
    setLoading(true);
    authFetch(`${API_BASE}/roles`)
      .then((res) => res.json())
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch(() => setMessage({ type: "error", text: "Could not load roles." }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const handleAddRole = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!roleName.trim()) {
      setMessage({ type: "error", text: "Please enter a role name." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/roles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ RoleName: roleName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to create role." });
        return;
      }

      setMessage({ type: "success", text: "Data successfully added." });
      setRoleName("");
      loadRoles();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while creating the role." });
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (r) => {
    setMessage(null);
    setEditingId(r.RoleID);
    setEditingName(r.RoleName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (id) => {
    if (!editingName.trim()) {
      setMessage({ type: "error", text: "Please enter a role name." });
      return;
    }
    setSavingEdit(true);
    try {
      const res = await authFetch(`${API_BASE}/roles/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ RoleName: editingName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to update role." });
        return;
      }

      setMessage({ type: "success", text: "Data successfully updated." });
      setEditingId(null);
      setEditingName("");
      loadRoles();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while updating the role." });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.RoleName}"? This can't be undone.`)) return;

    setMessage(null);
    setDeletingId(r.RoleID);
    try {
      const res = await authFetch(`${API_BASE}/roles/${r.RoleID}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to delete role." });
        return;
      }

      setMessage({ type: "success", text: "Role deleted." });
      loadRoles();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while deleting the role." });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 900 }}>
      <h4 className="master-page-title">Roles</h4>

      <div className="master-toolbar">
        <label htmlFor="roleName" className="master-toolbar-label">
          Role
        </label>
        <form className="master-toolbar-row" onSubmit={handleAddRole}>
          <input
            id="roleName"
            type="text"
            className="master-input"
            placeholder="e.g. Estimator"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
          />
          <button type="submit" className="master-add-btn" disabled={submitting}>
            {submitting ? "ADDING..." : "ADD"}
          </button>
        </form>

        {message && (
          <p className={`master-msg ${message.type === "success" ? "master-msg-success" : "master-msg-error"}`}>
            {message.text}
          </p>
        )}
      </div>

      <div className="master-table-wrap">
        <table className="master-table">
          <thead>
            <tr>
              <th>Role ID</th>
              <th>Role Name</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="text-center">Loading...</td>
              </tr>
            ) : roles.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center">No roles yet.</td>
              </tr>
            ) : (
              roles.map((r) => (
                <tr key={r.RoleID}>
                  <td>{r.RoleID}</td>
                  <td>
                    {editingId === r.RoleID ? (
                      <input
                        type="text"
                        className="master-edit-input"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      r.RoleName
                    )}
                  </td>
                  <td>
                    <div className="project-actions">
                      {editingId === r.RoleID ? (
                        <>
                          <button
                            className="icon-btn"
                            title="Save"
                            onClick={() => saveEdit(r.RoleID)}
                            disabled={savingEdit}
                          >
                            <CheckIcon />
                          </button>
                          <button className="icon-btn" title="Cancel" onClick={cancelEdit} disabled={savingEdit}>
                            <XIcon />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="icon-btn" title="View" onClick={() => setViewing(r)}>
                            <EyeIcon />
                          </button>
                          <button className="icon-btn" title="Edit" onClick={() => startEdit(r)}>
                            <EditIcon />
                          </button>
                          {/* Super Admin only per the Role Matrix -- Admin's
                              Master cell is Add-Edit, no Delete. Backend
                              enforces this too (require_tier("Super Admin")
                              on DELETE /roles/<id>). */}
                          {canDelete("Master") && (
                            <button
                              className="icon-btn"
                              title="Delete"
                              onClick={() => handleDelete(r)}
                              disabled={deletingId === r.RoleID}
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000
          }}
          onClick={() => setViewing(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 8, padding: "24px 28px", minWidth: 320, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ margin: "0 0 16px", color: "#154749", fontWeight: 700 }}>Role Details</h5>
            <p style={{ margin: "0 0 8px", fontSize: 14 }}><strong>Role ID:</strong> {viewing.RoleID}</p>
            <p style={{ margin: "0 0 20px", fontSize: 14 }}><strong>Role Name:</strong> {viewing.RoleName}</p>
            <div style={{ textAlign: "right" }}>
              <button type="button" className="master-add-btn" style={{ padding: "8px 20px" }} onClick={() => setViewing(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}